import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://neajcljcqswazzzwcpvn.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5lYWpjbGpjcXN3YXp6endjcHZuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTMzNjI3NjYsImV4cCI6MjA2ODkzODc2Nn0.JA1G5e-D3OTnEfrgvU7LQeR_HSgW7FZVxG1cYKNYwTI';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Funções de autenticação administrativa
export const adminAuth = {
  // Login do administrador
  async signIn(email, password) {
    try {
      const { data, error } = await supabase
        .from('admins')
        .select('*')
        .eq('email', email)
        .single();

      if (error || !data) {
        throw new Error('Credenciais inválidas');
      }

      // Verificar senha (em produção, usar bcrypt adequadamente)
      const { data: passwordCheck } = await supabase.rpc('verify_password', {
        email: email,
        password: password
      });

      if (!passwordCheck) {
        throw new Error('Credenciais inválidas');
      }

      // Salvar sessão no localStorage
      localStorage.setItem('admin_session', JSON.stringify({
        id: data.id,
        email: data.email,
        name: data.name,
        loginTime: new Date().toISOString()
      }));

      return { user: data, error: null };
    } catch (error) {
      return { user: null, error: error.message };
    }
  },

  // Logout do administrador
  signOut() {
    localStorage.removeItem('admin_session');
  },

  // Verificar se está logado
  getSession() {
    const session = localStorage.getItem('admin_session');
    if (session) {
      try {
        return JSON.parse(session);
      } catch {
        localStorage.removeItem('admin_session');
        return null;
      }
    }
    return null;
  },

  // Verificar se é administrador
  isAdmin() {
    return this.getSession() !== null;
  }
};

// Funções para gerenciar perguntas do quiz
export const quizService = {
  // Listar todas as perguntas
  async getQuestions() {
    const { data, error } = await supabase
      .from('quiz_questions')
      .select('*')
      .eq('active', true)
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  // Criar nova pergunta
  async createQuestion(question) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const { data, error } = await supabase
      .from('quiz_questions')
      .insert([{
        ...question,
        created_by: session.id
      }])
      .select()
      .single();

    return { data, error };
  },

  // Atualizar pergunta
  async updateQuestion(id, question) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const { data, error } = await supabase
      .from('quiz_questions')
      .update({
        ...question,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    return { data, error };
  },

  // Deletar pergunta
  async deleteQuestion(id) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const { data, error } = await supabase
      .from('quiz_questions')
      .update({ active: false })
      .eq('id', id);

    return { data, error };
  },

  // Submeter respostas do quiz
  async submitQuiz(participantName, participantEmail, answers, timeSpentSeconds) {
    try {
      // Verificar se já existe um participante com o mesmo nome (evitar duplicatas)
      const { data: existingParticipant } = await supabase
        .from('quiz_participants')
        .select('id, name, email, completed_at')
        .eq('name', participantName)
        .order('completed_at', { ascending: false })
        .limit(1);

      // Se já existe um participante recente (menos de 1 minuto), não criar novo
      if (existingParticipant && existingParticipant.length > 0) {
        const lastSubmission = new Date(existingParticipant[0].completed_at);
        const now = new Date();
        const timeDiff = (now - lastSubmission) / 1000; // diferença em segundos
        
        if (timeDiff < 60) { // menos de 1 minuto
          throw new Error('Você já participou recentemente. Aguarde um momento antes de tentar novamente.');
        }
      }

      // Criar participante (inserir sem time_spent_seconds por padrão)
      // A coluna será adicionada futuramente no banco de dados
      const { data: participant, error: participantError } = await supabase
        .from('quiz_participants')
        .insert([{
          name: participantName,
          email: participantEmail || null
        }])
        .select()
        .single();

      if (participantError) throw participantError;

      // Buscar perguntas para calcular pontuação
      const { data: questions, error: questionsError } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('active', true);

      if (questionsError) throw questionsError;

      let totalScore = 0;
      const answerRecords = [];

      // Processar cada resposta
      for (const answer of answers) {
        const question = questions.find(q => q.id === answer.questionId);
        if (question) {
          const isCorrect = question.correct_answer === answer.answer;
          const pointsEarned = isCorrect ? question.points : 0;
          totalScore += pointsEarned;

          answerRecords.push({
            participant_id: participant.id,
            question_id: answer.questionId,
            answer: answer.answer,
            is_correct: isCorrect,
            points_earned: pointsEarned
          });
        }
      }

      // Salvar respostas
      const { error: answersError } = await supabase
        .from('quiz_answers')
        .insert(answerRecords);

      if (answersError) throw answersError;

      // Atualizar pontuação do participante
      const { data: updatedParticipant, error: updateError } = await supabase
        .from('quiz_participants')
        .update({ score: totalScore })
        .eq('id', participant.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Tentar atualizar o tempo (se a coluna existir)
      if (timeSpentSeconds && timeSpentSeconds > 0) {
        try {
          await supabase
            .from('quiz_participants')
            .update({ time_spent_seconds: timeSpentSeconds })
            .eq('id', participant.id);
        } catch (timeError) {
          // Ignorar erro se a coluna não existir
          console.log('Coluna time_spent_seconds não disponível ainda');
        }
      }

      return { data: updatedParticipant, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  },

  // Obter ranking
  async getRanking() {
    try {
      // Tentar buscar com time_spent_seconds
      const { data, error } = await supabase
        .from('quiz_participants')
        .select('id, name, email, score, completed_at, time_spent_seconds')
        .order('score', { ascending: false })
        .order('time_spent_seconds', { ascending: true })
        .limit(50);

      if (error) {
        // Se der erro (coluna não existe), buscar sem time_spent_seconds
        console.log('Coluna time_spent_seconds não existe, buscando sem ela...');
        const { data: dataFallback, error: errorFallback } = await supabase
          .from('quiz_participants')
          .select('id, name, email, score, completed_at')
          .order('score', { ascending: false })
          .order('completed_at', { ascending: false })
          .limit(50);
        
        return { data: dataFallback, error: errorFallback };
      }

      return { data, error };
    } catch (err) {
      console.error('Erro ao buscar ranking:', err);
      return { data: null, error: err };
    }
  },

  // Deletar participante
  async deleteParticipant(participantId) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    try {
      // Primeiro, deletar as respostas do participante
      const { error: answersError } = await supabase
        .from('quiz_answers')
        .delete()
        .eq('participant_id', participantId);

      if (answersError) throw answersError;

      // Depois, deletar o participante
      const { data, error } = await supabase
        .from('quiz_participants')
        .delete()
        .eq('id', participantId);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  }
};

// Funções para gerenciar programação
export const scheduleService = {
  // Listar eventos
  async getEvents() {
    const { data, error } = await supabase
      .from('schedule_events')
      .select('*')
      .eq('active', true)
      .order('date', { ascending: true })
      .order('start_time', { ascending: true });
    
    return { data, error };
  },

  // Criar evento
  async createEvent(event) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const { data, error } = await supabase
      .from('schedule_events')
      .insert([{
        ...event,
        created_by: session.id
      }])
      .select()
      .single();

    return { data, error };
  },

  // Atualizar evento
  async updateEvent(id, event) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const { data, error } = await supabase
      .from('schedule_events')
      .update({
        ...event,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    return { data, error };
  },

  // Deletar evento
  async deleteEvent(id) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const { data, error } = await supabase
      .from('schedule_events')
      .update({ active: false })
      .eq('id', id);

    return { data, error };
  }
};

// Funções para gerenciar comunicados
export const announcementService = {
  // Listar comunicados
  async getAnnouncements() {
    const { data, error } = await supabase
      .from('announcements')
      .select('*')
      .eq('active', true)
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  // Criar comunicado
  async createAnnouncement(announcement) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    // Preparar dados para inserção
    const announcementData = {
      ...announcement,
      created_by: session.id
    };

    // Remover campos que não existem na tabela
    delete announcementData.schedule_date;
    delete announcementData.schedule_time;

    // Se estiver agendado, calcular o schedule_datetime
    if (announcement.scheduled && announcement.schedule_date && announcement.schedule_time) {
      // Combinar data e hora em um único timestamp
      const scheduleDateTime = new Date(`${announcement.schedule_date}T${announcement.schedule_time}:00`);
      announcementData.schedule_datetime = scheduleDateTime.toISOString();
    }

    const { data, error } = await supabase
      .from('announcements')
      .insert([announcementData])
      .select()
      .single();

    // Se deve enviar notificação imediatamente (não agendada)
    if (data && announcement.send_notification && (!announcement.scheduled || !announcement.schedule_date)) {
      await this.sendNotification(data.title, data.content, data.priority, data.id);
    }

    return { data, error };
  },
  
  // Enviar notificação
  async sendNotification(title, content, priority = 'normal', announcementId = null) {
    console.log(`Disparando envio de notificação: ${title}`);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: JSON.stringify({ 
          title, 
          body: content,
          announcement_id: announcementId
        })
      });

      if (error) throw error;

      console.log('Função de notificação invocada com sucesso:', data);
      return { success: true, data };
    } catch (error) {
      console.error('Erro ao invocar a função de notificação:', error);
      return { success: false, error };
    }
  },

  // Atualizar comunicado
  async updateAnnouncement(id, announcement) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    // Preparar dados para atualização
    const updateData = {
      ...announcement,
      updated_at: new Date().toISOString()
    };

    // Se estiver agendado, calcular o schedule_datetime
    if (announcement.scheduled && announcement.schedule_date && announcement.schedule_time) {
      // Combinar data e hora em um único timestamp
      const scheduleDateTime = new Date(`${announcement.schedule_date}T${announcement.schedule_time}:00`);
      updateData.schedule_datetime = scheduleDateTime.toISOString();
    } else if (!announcement.scheduled) {
      // Se não estiver agendado, limpar os campos de agendamento
      updateData.schedule_datetime = null;
    }

    const { data, error } = await supabase
      .from('announcements')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    // Se deve enviar notificação imediatamente (não agendada)
    if (data && announcement.send_notification && (!announcement.scheduled || !announcement.schedule_date)) {
      await this.sendNotification(data.title, data.content, data.priority, data.id);
    }

    return { data, error };
  },

  // Deletar comunicado
  async deleteAnnouncement(id) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const { data, error } = await supabase
      .from('announcements')
      .update({ active: false })
      .eq('id', id);

    return { data, error };
  },
  
  // Processar comunicados agendados
  async processScheduledAnnouncements() {
    try {
      const now = new Date().toISOString();
      
      // Buscar comunicados agendados que já passaram da data/hora de publicação
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('scheduled', true)
        .lt('schedule_datetime', now)
        .eq('active', false); // Ainda não ativados
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        console.log(`Processando ${data.length} comunicados agendados`);
        
        // Para cada comunicado agendado
        for (const announcement of data) {
          // Ativar o comunicado
          const { error: updateError } = await supabase
            .from('announcements')
            .update({ 
              active: true,
              updated_at: now
            })
            .eq('id', announcement.id);
          
          if (updateError) {
            console.error(`Erro ao ativar comunicado ${announcement.id}:`, updateError);
            continue;
          }
          
          console.log(`Comunicado ativado: ${announcement.title}`);
          
          // Enviar notificação se configurado
          if (announcement.send_notification) {
            await this.sendNotification(announcement.title, announcement.content, announcement.priority);
          }
        }
        
        return { success: true, processed: data.length };
      }
      
      return { success: true, processed: 0 };
    } catch (error) {
      console.error('Erro ao processar comunicados agendados:', error);
      return { success: false, error };
    }
  }
};

// Funções para gerenciar equipe de contato
export const contactService = {
  // Listar membros da equipe
  async getTeamMembers() {
    const { data, error } = await supabase
      .from('team_members')
      .select('*')
      .eq('active', true)
      .order('commission', { ascending: true })
      .order('name', { ascending: true });
    
    return { data, error };
  },

  // Criar membro da equipe
  async createTeamMember(member) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const { data, error } = await supabase
      .from('team_members')
      .insert([{
        ...member,
        created_by: session.id
      }])
      .select()
      .single();

    return { data, error };
  },

  // Atualizar membro da equipe
  async updateTeamMember(id, member) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const { data, error } = await supabase
      .from('team_members')
      .update({
        ...member,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    return { data, error };
  },

  // Deletar membro da equipe
  async deleteTeamMember(id) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const { data, error } = await supabase
      .from('team_members')
      .update({ active: false })
      .eq('id', id);

    return { data, error };
  },

  async uploadTeamMemberPhoto(file) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const fileExt = file.name.split('.').pop();
    const uniqueId = Math.random().toString(36).substring(2, 10);
    const filePath = `members/${Date.now()}-${uniqueId}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('team-photos')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) throw uploadError;

    const { data: publicData, error: publicError } = supabase.storage
      .from('team-photos')
      .getPublicUrl(filePath);

    if (publicError) throw publicError;

    return publicData?.publicUrl;
  }
};

// Funções para gerenciar notificações push
export const pushNotificationService = {
  // Listar todas as inscrições de dispositivos
  async getSubscriptions() {
    const { data, error } = await supabase
      .from('push_subscriptions')
      .select('*')
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  // Listar logs de notificações
  async getNotificationLogs(limit = 100) {
    const { data, error } = await supabase
      .from('notification_logs')
      .select(`
        *,
        announcements(title),
        push_subscriptions(id, created_at)
      `)
      .order('sent_at', { ascending: false })
      .limit(limit);
    
    return { data, error };
  },

  // Obter estatísticas de notificações
  async getNotificationStats() {
    try {
      const { data: logs, error } = await supabase
        .from('notification_logs')
        .select('status, sent_at');

      if (error) throw error;

      const total = logs?.length || 0;
      const success = logs?.filter(log => log.status === 'success').length || 0;
      const failed = logs?.filter(log => log.status === 'failed').length || 0;

      // Calcular estatísticas das últimas 24 horas
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const last24h = logs?.filter(log => new Date(log.sent_at) > oneDayAgo).length || 0;

      return {
        data: {
          total,
          success,
          failed,
          successRate: total > 0 ? ((success / total) * 100).toFixed(1) : 0,
          last24h
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Deletar uma inscrição
  async deleteSubscription(id) {
    const session = adminAuth.getSession();
    if (!session) throw new Error('Não autorizado');

    const { data, error } = await supabase
      .from('push_subscriptions')
      .delete()
      .eq('id', id);

    return { data, error };
  }
};
